import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, ExternalLink, Clock, CheckCircle2, XCircle, RotateCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  submitted: { label: "New", color: "bg-primary/10 text-primary", icon: Clock },
  in_progress: { label: "In progress", color: "bg-amber-100 text-amber-800", icon: RotateCw },
  delivered: { label: "Delivered", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", color: "bg-slate-100 text-slate-600", icon: XCircle },
};

export default function AdminWhiteLabel() {
  const { token } = useAuth();
  const [orders, setOrders] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = useCallback(() => {
    axios
      .get(`${API}/api/whitelabel-orders`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setOrders(r.data.orders || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load orders."));
  }, [token]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const patch = async (id, status, admin_notes) => {
    try {
      await axios.patch(
        `${API}/api/whitelabel-orders/${id}`,
        { status, admin_notes: admin_notes ?? "" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Marked as ${status.replace("_", " ")}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update.");
    }
  };

  if (!orders) {
    return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-whitelabel-page">
      <div className="max-w-5xl mx-auto p-6 lg:p-10">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft size={14} /> Back to Admin
        </Link>
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="label-caps mb-2 text-primary">White-Label</div>
            <h1 className="font-serif text-4xl">Onboarding queue</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Each row is a paying customer who's submitted their branding info. Click a row to see all details and update status.
            </p>
          </div>
          <div className="text-sm">
            <span className="font-serif text-3xl mr-1">{orders.length}</span>
            <span className="text-muted-foreground">orders</span>
          </div>
        </div>

        {orders.length === 0 ? (
          <div className="border border-dashed border-border rounded-lg p-12 text-center text-muted-foreground text-sm" data-testid="admin-wl-empty">
            No white-label orders yet. New submissions will appear here after checkout + onboarding form.
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => {
              const meta = STATUS_META[o.status] || STATUS_META.submitted;
              const Icon = meta.icon;
              const isOpen = expanded === o.id;
              return (
                <div
                  key={o.id}
                  className="border border-border rounded-lg bg-card overflow-hidden"
                  data-testid={`admin-wl-row-${o.id}`}
                >
                  <button
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-secondary/40 transition-colors"
                    onClick={() => setExpanded(isOpen ? null : o.id)}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="font-serif text-lg truncate">{o.business_name}</div>
                        <span
                          className="h-4 w-4 rounded-sm border border-border shrink-0"
                          style={{ backgroundColor: o.primary_color }}
                          title={o.primary_color}
                        />
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${meta.color}`}>
                          <Icon size={11} /> {meta.label}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {o.admin_email} · {o.contact_phone || "no phone"} · submitted{" "}
                        {o.created_at ? new Date(o.created_at).toLocaleDateString() : ""}
                      </div>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-border p-5 bg-secondary/30 space-y-4">
                      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                        <DetailRow label="Buyer name" value={o.user_name || "—"} />
                        <DetailRow label="Buyer email" value={o.user_email} />
                        <DetailRow label="Business name" value={o.business_name} />
                        <DetailRow label="Admin email" value={o.admin_email} />
                        <DetailRow label="Phone" value={o.contact_phone || "—"} />
                        <DetailRow
                          label="Brand color"
                          value={
                            <span className="inline-flex items-center gap-2">
                              <span className="h-4 w-4 rounded-sm border border-border" style={{ backgroundColor: o.primary_color }} />
                              <code className="text-xs">{o.primary_color}</code>
                            </span>
                          }
                        />
                        <DetailRow
                          label="Logo URL"
                          value={
                            o.logo_url ? (
                              <a href={o.logo_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary underline">
                                Open <ExternalLink size={11} />
                              </a>
                            ) : (
                              "— (not provided)"
                            )
                          }
                        />
                        <DetailRow
                          label="Preferred subdomain"
                          value={o.preferred_subdomain ? `${o.preferred_subdomain}.dynamicfinancialsws.online` : "—"}
                        />
                      </div>
                      {o.notes && (
                        <div className="pt-3 border-t border-border/60">
                          <div className="text-xs text-muted-foreground mb-1">Buyer notes</div>
                          <div className="text-sm whitespace-pre-wrap">{o.notes}</div>
                        </div>
                      )}
                      <div className="pt-3 border-t border-border/60">
                        <div className="text-xs text-muted-foreground mb-2">Internal notes (only you can see)</div>
                        <Textarea
                          rows={2}
                          defaultValue={o.admin_notes || ""}
                          onChange={(e) => setDrafts((d) => ({ ...d, [o.id]: e.target.value }))}
                          placeholder="e.g. Provisioned Oct 3 · SSL pending · handed off to onboarding team"
                          data-testid={`admin-wl-notes-${o.id}`}
                        />
                      </div>
                      <div className="flex flex-wrap gap-2 pt-2">
                        <Button
                          size="sm"
                          variant={o.status === "in_progress" ? "default" : "outline"}
                          onClick={() => patch(o.id, "in_progress", drafts[o.id] ?? o.admin_notes)}
                          data-testid={`admin-wl-inprogress-${o.id}`}
                        >
                          Mark in progress
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => patch(o.id, "delivered", drafts[o.id] ?? o.admin_notes)}
                          data-testid={`admin-wl-delivered-${o.id}`}
                        >
                          Mark delivered
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => patch(o.id, "cancelled", drafts[o.id] ?? o.admin_notes)}
                          data-testid={`admin-wl-cancelled-${o.id}`}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => patch(o.id, o.status, drafts[o.id] ?? "")}
                          data-testid={`admin-wl-savenotes-${o.id}`}
                        >
                          Save notes only
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const DetailRow = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">{label}</span>
    <span className="text-sm break-words">{value}</span>
  </div>
);
